import type { DataAccessRequestStatus } from '@repo/api-codegen';
import { EntityType } from '@repo/common/enums/entityType';
import type { IconNames } from '@repo/foundations';
import type { ColorNames } from '@repo/theme/utils';
import type { EventType } from './types';
import {
	TEAM_ACCESS_REQUEST_APPROVED_EVENT,
	TEAM_ACCESS_REQUEST_CREATED_EVENT,
	TEAM_ACCESS_REQUEST_REJECTED_EVENT,
} from './utils/teamAccessRequestUtils';

export const APP_HEADER_HEIGHT = 58;

// Notification related constants
export const NOTIFICATION_STALE_TIME = 1000 * 60 * 2; // 2 minutes

// Notification messages
export const NOTIFICATION_MESSAGES = {
	DELETE_SUCCESS: 'Notification deleted successfully',
	DELETE_ERROR: 'Failed to delete notification',
	DELETE_ALL_SUCCESS: 'All notifications deleted successfully',
	DELETE_ALL_ERROR: 'Failed to delete all notifications',
	RESTORE_SUCCESS: 'Notification restored successfully',
	RESTORE_ERROR: 'Failed to restore notification',
} as const;

// Analytics event names
export const ANALYTICS_EVENTS = {
	// Existing events
	DELETE_ALL: 'inbox/delete_all/click',
	DELETE_READ: 'inbox/delete_read/click',
	DELETE: 'inbox/delete/click',
	MARK_ALL_READ: 'inbox/all_read/click',
	FILTER: 'inbox/filter/click',
	SEARCH: 'inbox/search/click',

	// List item events
	NOTIFICATION_CLICK: 'inbox/notification/click',
	NOTIFICATION_CONTEXT_MENU: 'inbox/notification/context_menu',
	NOTIFICATION_CONTEXT_MENU_OPEN: 'inbox/notification/context_menu_open',
	NOTIFICATION_CONTEXT_MENU_CLOSE: 'inbox/notification/context_menu_close',
	NOTIFICATION_MARK_READ: 'inbox/notification/mark_read',
	NOTIFICATION_DELETE: 'inbox/notification/delete',
	NOTIFICATION_TURN_OFF: 'inbox/notification/turn_off',
	NOTIFICATION_TURN_OFF_UNDO: 'inbox/notification/turn_off_undo',
	NOTIFICATION_DELETE_UNDO: 'inbox/notification/delete_undo',

	// Header actions events
	SHOW_READ_TOGGLE: 'inbox/show_read/toggle',
	SETTINGS_NAVIGATE: 'inbox/settings/navigate',

	// Filter events
	FILTER_OPEN: 'inbox/filter/open',
	FILTER_CLOSE: 'inbox/filter/close',
	FILTER_SEARCH: 'inbox/filter/search',
	FILTER_TOGGLE: 'inbox/filter/toggle',

	// Search events
	SEARCH_OPEN: 'inbox/search/open',
	SEARCH_CLOSE: 'inbox/search/close',
	SEARCH_QUERY: 'inbox/search/query',

	// View header events
	BREADCRUMB_CLICK: 'inbox/breadcrumb/click',

	// Task action events
	TASK_ACTION_CLICK: 'inbox/task_action/click',
	CHANGE_REQUEST_APPLY: 'inbox/change_request/apply',
	ACCESS_REQUEST_APPLY: 'inbox/access_request/apply',
	INCIDENT_STATUS_UPDATE: 'inbox/incident/status_update',
	DATA_ACCESS_REQUEST_VIEW: 'inbox/data_access_request/view',
} as const;

export const DESCRIPTION_UPDATED_EVENTS = new Set([
	'resource_definition_updated',
	'resource_description_updated',
]);

export const OWNER_ASSIGNED_EVENTS = new Set(['resource_owner_assigned']);

export const COLLECTION_ADDED_REMOVED_EVENTS = new Set([
	'resource_collection_added',
	'resource_collection_removed',
]);

export const RESOURCE_TAG_ADDED_REMOVED_EVENTS = new Set([
	'resource_tag_added',
	'resource_tag_removed',
]);

// Integrations – per spec, only sync failures are considered in Inbox
export const INTEGRATION_JOB_EVENTS = new Set([
	'extraction_job_failed',
	'metadata_push_job_failed',
	'integration_failed',
]);

export const CHANGE_REQUEST_EVENTS = new Set([
	'change_request',
	'applied_change_request',
]);

export const BASIC_RESOURCE_EVENTS = new Set([
	'access_request',
	'applied_access_request',
	TEAM_ACCESS_REQUEST_CREATED_EVENT,
	TEAM_ACCESS_REQUEST_APPROVED_EVENT,
	TEAM_ACCESS_REQUEST_REJECTED_EVENT,
	'resource_published',
	'resource_unpublished',
	'resource_archived',
	'mentions',
	'table_schema_changed',
	'tables_dropped',
]);

export const QUESTION_EVENTS = new Set([
	'question_new',
	'question_answer',
	'question_reply',
	'question_reply_removed',
	'question_owner_assigned',
]);

export const MONITOR_INCIDENT_EVENTS = new Set([
	'monitor_incident_created',
	'monitor_incident_resolved',
]);

export const COMMENT_EVENTS = new Set([
	'comment_new_thread',
	'comment_reply',
	'comment_mention',
	'comment_reaction',
	'comment_resolved',
]);

export const ANNOUNCEMENT_EVENTS = new Set(['announcement']);

export const DATA_ACCESS_REQUEST_EVENTS = new Set([
	'data_access_request_created',
	'data_access_request_approved',
	'data_access_request_approved_admin',
	'data_access_request_rejected',
	'data_access_request_cancelled',
	'data_access_request_expiring_soon',
	'data_access_request_expired',
]);

export const DEFAULT_ICON: { icon: IconNames; color: ColorNames } = {
	icon: 'infoCircle',
	color: 'icon/primary/default',
};

export const RESOURCE_TYPE_ICON_MAP: Partial<Record<EntityType, IconNames>> = {
	[EntityType.document]: 'file',
	[EntityType.glossary]: 'file',
	[EntityType.dashboard]: 'customDashboard',
	[EntityType.job]: 'cylinder',
	[EntityType.event]: 'click',
	[EntityType.schema]: 'schema',
	[EntityType.database]: 'database',
	[EntityType.dashboard_group]: 'folder',
	[EntityType.job_group]: 'folder',
	[EntityType.category]: 'folder',
	[EntityType.generic]: 'folder',
	[EntityType.generic_group]: 'folder',
	[EntityType.table]: 'table',
	[EntityType.column]: 'columns',
	[EntityType.chart]: 'chartBar',
	[EntityType.collection]: 'listDetails',
	[EntityType.dictionary_term]: 'book',
	[EntityType.metric]: 'chartHistogram',
	[EntityType.question]: 'messageCircleQuestion',
	[EntityType.question_reply]: 'messageCircleQuestion',
	[EntityType.user_group]: 'users',
};

export const DATA_ACCESS_REQUEST_NOTIFICATION_STATUS_MAP: Record<
	string,
	DataAccessRequestStatus
> = {
	data_access_request_created: 'pending',
	data_access_request_approved: 'approved',
	data_access_request_approved_admin: 'approved',
	data_access_request_rejected: 'rejected',
	data_access_request_cancelled: 'cancelled',
	data_access_request_expiring_soon: 'approved',
	data_access_request_expired: 'expired',
};

export const DATA_ACCESS_REQUEST_STATUS_ICON_MAP: Record<
	DataAccessRequestStatus,
	{ iconName: IconNames; color?: ColorNames; fillColor?: ColorNames }
> = {
	deleted: {
		iconName: 'circleCheckFilled',
		fillColor: 'icon/secondary/default',
	},
	pending: { iconName: 'clock', color: 'icon/info/default' },
	approved: {
		iconName: 'circleCheckFilled',
		fillColor: 'icon/success/default',
	},
	rejected: {
		iconName: 'alertTriangleFilled',
		fillColor: 'icon/warning/default',
	},
	expired: { iconName: 'circleXFilled', fillColor: 'icon/primary/disabled' },
	cancelled: {
		iconName: 'alertTriangleFilled',
		fillColor: 'icon/warning/default',
	},
};

export const NOTIFICATION_EVENT_TYPES: EventType[] = [
	{
		label: 'Access requests',
		value: 'notify_requests',
		values: [
			TEAM_ACCESS_REQUEST_CREATED_EVENT,
			TEAM_ACCESS_REQUEST_APPROVED_EVENT,
			TEAM_ACCESS_REQUEST_REJECTED_EVENT,
			'data_access_request_created',
			'data_access_request_approved',
			'data_access_request_approved_admin',
			'data_access_request_rejected',
			'data_access_request_cancelled',
			'data_access_request_expiring_soon',
			'data_access_request_expired',
		],
		iconName: 'lock',
	},
	{
		label: 'Announcements',
		value: 'notify_announcement',
		values: ['announcement', 'free_trial_ending'],
		iconName: 'speakerphone',
	},
	{
		label: 'Comments and replies',
		value: 'notify_comments',
		values: [
			'comment_new_thread',
			'comment_reply',
			'comment_mention',
			'comment_reaction',
			'comment_resolved',
			'comment_delete',
		],
		iconName: 'message',
	},
	{
		label: 'Integrations',
		value: 'notify_integrations',
		values: [
			// Sync failed (pull and push)
			'extraction_job_failed',
			'metadata_push_job_failed',
			'integration_failed',
		],
		iconName: 'plugConnected',
	},
	{
		label: 'Questions',
		value: 'notify_questions',
		values: [
			'question_owner_assigned',
			'question_answer',
			'question_new',
			'question_reply',
			'question_reply_removed',
			'question_group_assigned',
		],
		iconName: 'messageCircleQuestion',
	},
	{
		label: 'Mentions',
		value: 'notify_mentions',
		values: ['mentions'],
		iconName: 'at',
	},
	{
		label: 'Monitors',
		value: 'notify_monitors',
		values: ['monitor_incident_created', 'monitor_incident_resolved'],
		iconName: 'alertTriangle',
		isTask: true,
	},
	{
		label: 'Resource updates',
		value: 'notify_resource_updates',
		values: [
			'applied_access_request',
			'applied_change_request',
			'access_request',
			'change_request',
			'resource_archived',
			'resource_collection_added',
			'resource_collection_removed',
			'resource_definition_updated',
			'resource_description_updated',
			'resource_owner_assigned',
			'resource_published',
			'resource_tag_added',
			'resource_tag_removed',
			'resource_unpublished',
			'table_schema_changed',
			'tables_dropped',
		],
		iconName: 'file',
	},
	{
		label: 'Automation',
		value: 'notify_automation_review',
		values: ['automation_review'],
		iconName: 'bolt',
	},
	{
		label: 'Agents',
		value: 'notify_ai_agents',
		values: [
			'ai_task_run_completed',
			'ai_task_run_failed',
			'ai_task_run_timeout',
		],
		iconName: 'brain',
	},
	// Comment back in if we want to show security notifications in the inbox
	// Security notifications are email-only per spec; exclude from Inbox filters
	// {
	// 	label: 'Security',
	// 	value: 'notify_security',
	// 	values: ['api_key_created', 'joined_team'],
	// 	iconName: 'shield',
	// },
];
